import React, { useState } from 'react';
import '../styles/EmailSignup.css';
import { trackLeadCaptureEvent } from '../leadCaptureAnalytics.js';

/**
 * Email Signup Component
 * Captures email signups for product updates and special offers
 */
export default function EmailSignup({ context = 'landing', inline = false }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('');

    try {
      // Validate email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email || !emailRegex.test(email)) {
        throw new Error('Please enter a valid email address');
      }

      // Track attempt
      trackLeadCaptureEvent('email_signup_attempt', {
        source: context,
        calculatorTier: 'none'
      });

      // Save to backend
      const response = await fetch('/api/email-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email,
          source: context,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          referrer: document.referrer
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save email');
      }

      if (window.gtag) {
        window.gtag('event', 'email_signup', {
          event_category: 'Email',
          event_label: context,
          value: 1
        });
      }
      trackLeadCaptureEvent('email_signup_captured', {
        source: context,
        calculatorTier: 'none',
        leadStored: true
      });

      // Success
      setStatus('success');
      setMessage('Thanks! You\'re on the list. We\'ll keep you updated.');
      setEmail('');

      // Track success in Sentry
      if (window.Sentry) {
        window.Sentry.captureMessage('Email signup success', {
          level: 'info',
          tags: { email: email, source: context }
        });
      }

    } catch (error) {
      setStatus('error');
      setMessage(error.message || 'Something went wrong. Please try again.');
      trackLeadCaptureEvent('email_signup_failed', {
        source: context,
        calculatorTier: 'none',
        leadStored: false,
        error: error.message
      });
      
      // Log error to Sentry
      if (window.Sentry) {
        window.Sentry.captureException(error, {
          tags: { component: 'EmailSignup', source: context }
        });
      }
    }
  };

  if (inline) {
    return (
      <div className="email-signup-inline">
        <form onSubmit={handleSubmit} className="inline-form">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your.email@example.com"
            disabled={status === 'loading' || status === 'success'}
            className="inline-input"
            required
          />
          <button 
            type="submit" 
            disabled={status === 'loading' || status === 'success'}
            className="inline-button"
          >
            {status === 'loading' ? 'Saving...' : status === 'success' ? '✓ Signed Up' : 'Notify Me'}
          </button>
        </form>
        {(status === 'success' || status === 'error') && (
          <p className={`message ${status}`}>{message}</p>
        )}
      </div>
    );
  }

  return (
    <div className="email-signup-card">
      <div className="signup-icon">📧</div>
      <h3>Stay Updated</h3>
      <p className="signup-description">
        Get notified about new features, calculator updates, and special offers.
      </p>
      
      <form onSubmit={handleSubmit} className="signup-form">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your.email@example.com"
          disabled={status === 'loading' || status === 'success'}
          className="email-input"
          required
        />
        
        <button 
          type="submit" 
          disabled={status === 'loading' || status === 'success'}
          className="submit-button"
        >
          {status === 'loading' && '⏳ Saving...'}
          {status === 'success' && '✓ You\'re on the list!'}
          {status === 'idle' && '🚀 Notify Me'}
          {status === 'error' && '↻ Try Again'}
        </button>
      </form>

      {status === 'success' && (
        <div className="success-message">
          <p className="success-text">{message}</p>
          <p className="success-subtext">Check your email for confirmation.</p>
        </div>
      )}
      
      {status === 'error' && (
        <div className="error-message">
          <p className="error-text">{message}</p>
          <p className="error-subtext">Or email us: support@supercalcpro.com.au</p>
        </div>
      )}

      <p className="privacy-note">
        We respect your privacy. No spam, unsubscribe anytime.
      </p>
    </div>
  );
}













